import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import { Notifier } from '../../../system/services/notificationManager';
import { serverConfig } from '../../../../helpers/config';

interface PropertyImageUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onImageUploaded: (imageUrl: string) => void;
  currentImageUrl?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-upload-tabpanel-${index}`}
      aria-labelledby={`image-upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Allowed image formats (defined outside component to avoid dependency issues)
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

export function PropertyImageUploadDialog({
  open,
  onClose,
  onImageUploaded,
  currentImageUrl,
}: PropertyImageUploadDialogProps) {
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(
    currentImageUrl || null,
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    setImageUrl('');
    setSelectedFile(null);
    setPreview(null);
  };

  const handleFileSelect = useCallback((file: File) => {
    // Check if file format is allowed
    const isAllowedImageFormat = (f: File): boolean => {
      // Check MIME type
      if (!ALLOWED_IMAGE_TYPES.includes(f.type.toLowerCase())) {
        return false;
      }

      // Also check file extension
      const extension = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
      return ALLOWED_IMAGE_EXTENSIONS.includes(extension);
    };

    if (!isAllowedImageFormat(file)) {
      Notifier.error('Only JPG, JPEG, PNG, and WebP image formats are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Notifier.error('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const isAllowedImageUrl = (url: string): boolean => {
    const extension = url.toLowerCase().substring(url.lastIndexOf('.'));
    const queryIndex = extension.indexOf('?');
    const cleanExtension =
      queryIndex !== -1 ? extension.substring(0, queryIndex) : extension;
    return ALLOWED_IMAGE_EXTENSIONS.includes(cleanExtension);
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setImageUrl(url);
    if (url && isValidUrl(url)) {
      if (isAllowedImageUrl(url)) {
        setPreview(url);
      } else {
        setPreview(null);
      }
    } else {
      setPreview(null);
    }
  };

  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleUpload = async () => {
    if (tab === 0 && !selectedFile) {
      Notifier.error('Please select an image file');
      return;
    }

    if (tab === 1 && !imageUrl.trim()) {
      Notifier.error('Please enter an image URL');
      return;
    }

    if (tab === 1 && !isValidUrl(imageUrl.trim())) {
      Notifier.error('Please enter a valid URL');
      return;
    }

    if (tab === 1 && !isAllowedImageUrl(imageUrl.trim())) {
      Notifier.error('Only JPG, JPEG, PNG, and WebP image formats are allowed');
      return;
    }

    setUploading(true);

    try {
      const config = await serverConfig;
      const { serverUrl } = config.getServerConnection();
      const formData = new FormData();

      if (tab === 0 && selectedFile) {
        // File upload
        formData.append('file', selectedFile);
        formData.append('folder', 'properties');
      } else if (tab === 1 && imageUrl.trim()) {
        // URL upload
        formData.append('url', imageUrl.trim());
        formData.append('folder', 'properties');
      }

      // Add old image URL for deletion if it exists
      if (currentImageUrl) {
        formData.append('oldImageUrl', currentImageUrl);
      }

      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await axios.post(
        `${serverUrl}/api/upload/image`,
        formData,
      );

      if (response.data.success && response.data.data?.url) {
        Notifier.success('Image uploaded successfully');
        onImageUploaded(response.data.data.url);
        handleClose();
      } else {
        throw new Error(
          response.data?.error ||
            response.data?.message ||
            'Upload failed: Invalid response',
        );
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // Extract error message from various possible locations
      let errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        (error.response?.status
          ? `Upload failed with status ${error.response.status}`
          : 'Failed to upload image. Please check your connection and try again.');

      // Check for file size errors specifically
      if (
        errorMessage.includes('File size') ||
        errorMessage.includes('LIMIT_FILE_SIZE') ||
        errorMessage.includes('exceeds the maximum limit')
      ) {
        errorMessage = 'File size exceeds the maximum limit of 5MB';
      }

      Notifier.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setTab(0);
      setImageUrl('');
      setSelectedFile(null);
      setPreview(null);
      setDragActive(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          width: '500px',
        },
      }}
    >
      <DialogTitle>Upload Property Image</DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="image upload tabs"
          sx={{
            mb: 2,
            height: '50px',
            '& .MuiTabs-indicator': {
              backgroundColor: '#36AFA5',
            },
          }}
        >
          <Tab
            icon={<CloudUploadIcon />}
            iconPosition="start"
            label="Upload File"
            id="image-upload-tab-0"
            aria-controls="image-upload-tabpanel-0"
            sx={{
              '&.Mui-selected': {
                color: '#36AFA5',
              },
            }}
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label="From URL"
            id="image-upload-tab-1"
            aria-controls="image-upload-tabpanel-1"
            sx={{
              '&.Mui-selected': {
                color: '#36AFA5',
              },
            }}
          />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              id="file-upload-input"
            />
            <label htmlFor="file-upload-input">
              <Paper
                component="div"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                sx={{
                  width: '100%',
                  minHeight: '200px',
                  border: dragActive
                    ? '2px dashed #36AFA5'
                    : '2px dashed #36AFA5',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: dragActive
                    ? 'action.hover'
                    : 'background.paper',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {preview ? (
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        borderRadius: 4,
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {selectedFile?.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <CloudUploadIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      Drag and drop an image here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or click to select a file
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Maximum file size: 5MB
                    </Typography>
                  </Box>
                )}
              </Paper>
            </label>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Image URL"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={handleUrlChange}
              error={imageUrl !== '' && !isValidUrl(imageUrl)}
              helperText={
                imageUrl !== '' && !isValidUrl(imageUrl)
                  ? 'Please enter a valid URL'
                  : imageUrl !== '' &&
                      isValidUrl(imageUrl) &&
                      !isAllowedImageUrl(imageUrl)
                    ? 'Only JPG, JPEG, PNG, and WebP formats are allowed'
                    : 'Enter the URL of the image you want to upload (JPG, JPEG, PNG, or WebP)'
              }
            />
            {preview && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview:
                </Typography>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                  }}
                  onError={() => {
                    setPreview(null);
                    Notifier.error('Failed to load image from URL');
                  }}
                />
              </Box>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="secondary"
          sx={{ color: 'white' }}
          disabled={
            uploading ||
            (tab === 0 && !selectedFile) ||
            (tab === 1 && (!imageUrl.trim() || !isValidUrl(imageUrl.trim())))
          }
          startIcon={uploading ? <CircularProgress size={20} /> : <ImageIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
